import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, EmptyState, Money, LineItemsEditor, computeTotals } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Loader2, Play, Repeat } from "lucide-react";

export default function Recurring() {
  const { companyId, cur } = useCompany();
  const [items, setItems] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ customer_id: "", lines: [{ product_id: "", name: "Abonnement mensuel", qty: 1, unit_price: 0, tax_rate: 0 }] });

  const load = () => api.get("/recurring-invoices", { params: { company_id: companyId } }).then(({ data }) => setItems(data.recurring));
  useEffect(() => {
    if (!companyId) return; load();
    api.get("/customers", { params: { company_id: companyId } }).then(({ data }) => setCustomers(data.customers));
    api.get("/products", { params: { company_id: companyId } }).then(({ data }) => setProducts(data.products));
  }, [companyId]);

  const totals = computeTotals(form.lines);
  const create = async () => {
    if (!form.customer_id) return toast.error("Choisissez un client");
    setSaving(true);
    try { await api.post("/recurring-invoices", form, { params: { company_id: companyId } }); toast.success("Abonnement créé");
      setOpen(false); setForm({ customer_id: "", lines: [{ product_id: "", name: "Abonnement mensuel", qty: 1, unit_price: 0, tax_rate: 0 }] }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const toggle = async (id) => { await api.post(`/recurring-invoices/${id}/toggle`, {}, { params: { company_id: companyId } }); load(); };
  const run = async () => {
    try { const { data } = await api.post("/recurring/run", {}, { params: { company_id: companyId } });
      toast.success(`${data.generated} facture(s) d'abonnement générée(s) + demande de paiement envoyée`); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="recurring-page">
      <PageHeader title="Factures récurrentes" subtitle="Abonnements mensuels : génération auto + demande de paiement Mobile Money. Exécution quotidienne planifiée." action={
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={run} data-testid="run-recurring-button"><Play className="w-4 h-4 mr-1" />Générer maintenant</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full" data-testid="new-recurring-button"><Plus className="w-4 h-4 mr-1" />Nouvel abonnement</Button></DialogTrigger>
            <DialogContent className="bg-popover max-w-3xl">
              <DialogHeader><DialogTitle className="font-display">Nouvel abonnement</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1"><Label>Client *</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="recurring-customer-select">
                    <option value="">— Choisir —</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select></div>
                <LineItemsEditor lines={form.lines} setLines={(l) => setForm({ ...form, lines: l })} products={products} />
                <div className="text-right font-display font-bold">Montant mensuel <Money value={totals.total} cur={cur} /></div>
              </div>
              <DialogFooter><Button onClick={create} disabled={saving} className="rounded-full" data-testid="save-recurring-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>} />
      <Card className="rounded-md border-border shadow-sm overflow-hidden">
        {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState title="Aucun abonnement" hint="Créez un abonnement mensuel récurrent." />
          : <Table>
              <TableHeader><TableRow><TableHead>Client</TableHead><TableHead className="text-right">Montant / mois</TableHead><TableHead>Prochaine échéance</TableHead><TableHead>Actif</TableHead></TableRow></TableHeader>
              <TableBody>{items.map((r) => (
                <TableRow key={r.id} data-testid={`recurring-row-${r.id}`}>
                  <TableCell className="font-medium flex items-center gap-2"><Repeat className="w-4 h-4 text-primary" />{r.customer_name}</TableCell>
                  <TableCell className="text-right"><Money value={r.total} cur={cur} /></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(r.next_run)}</TableCell>
                  <TableCell><Switch checked={r.active} onCheckedChange={() => toggle(r.id)} data-testid={`toggle-${r.id}`} /></TableCell>
                </TableRow>))}</TableBody>
            </Table>}
      </Card>
    </div>
  );
}
